import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { initCtiContextListener } from '../services/sap-context';
import { AuthGate } from '../components/auth/AuthGate';
import { AuthContextValue } from './auth/auth.types';
import { useAuthProviderState } from './auth/useAuthProviderState';

export type { User, AuthState, AuthContextValue } from './auth/auth.types';

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { state, signInWithNice, logout, refreshToken, bootstrapAuth, clearRefreshTimer } =
    useAuthProviderState();

  useEffect(() => {
    initCtiContextListener();
  }, []);

  useEffect(() => {
    bootstrapAuth();
  }, [bootstrapAuth]);

  useEffect(() => {
    return () => clearRefreshTimer();
  }, [clearRefreshTimer]);

  const value: AuthContextValue = {
    ...state,
    signInWithNice,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      <AuthGate
        isLoading={state.isLoading}
        needsNiceLink={state.needsNiceLink}
        isAuthenticated={state.isAuthenticated}
        error={state.error}
        onSignInWithNice={signInWithNice}
      >
        {children}
      </AuthGate>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
